import { useState } from 'react';
import { KeyRound, Search, ShieldCheck, Store, Truck, UserRound, Users as UsersIcon } from 'lucide-react';
import { UserRole, UserStatus } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { useAuth } from '@/lib/auth-store';
import { cn, formatFecha } from '@/lib/cn';
import {
  useCambiarEstadoUsuario,
  useRestablecerPassword,
  useUsuariosAdmin,
  type Credenciales,
  type UsuarioAdmin,
} from '@/lib/queries';
import {
  Boton,
  Cargando,
  ConfirmarModal,
  Entrada,
  ErrorCarga,
  Modal,
  Seleccion,
  Vacio,
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { TarjetaCredenciales } from '@/components/TarjetaCredenciales';

const ROLES: { valor: UserRole; texto: string; icono: typeof UserRound }[] = [
  { valor: UserRole.CUSTOMER, texto: 'Clientes', icono: UserRound },
  { valor: UserRole.MERCHANT_OWNER, texto: 'Propietarios', icono: Store },
  { valor: UserRole.MERCHANT_STAFF, texto: 'Empleados', icono: Store },
  { valor: UserRole.COURIER, texto: 'Domiciliarios', icono: Truck },
  { valor: UserRole.ADMIN, texto: 'Administración', icono: ShieldCheck },
];

const ESTADOS: Record<UserStatus, { texto: string; color: string }> = {
  [UserStatus.ACTIVE]: { texto: 'Activo', color: 'bg-emerald-100 text-emerald-800' },
  [UserStatus.SUSPENDED]: { texto: 'Suspendido', color: 'bg-red-100 text-red-800' },
  [UserStatus.PENDING_VERIFICATION]: { texto: 'Sin verificar', color: 'bg-amber-100 text-amber-800' },
};

export function UsuariosAdmin() {
  const [busqueda, setBusqueda] = useState('');
  const [rol, setRol] = useState('');
  const [estado, setEstado] = useState('');
  const [credenciales, setCredenciales] = useState<Credenciales | null>(null);

  const usuarios = useUsuariosAdmin({
    q: busqueda || undefined,
    role: rol || undefined,
    status: estado || undefined,
  });

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-3xl text-kick-dark">Usuarios</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Todas las cuentas de la plataforma, de cualquier rol.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray" />
          <Entrada
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre, usuario o teléfono"
            aria-label="Buscar usuarios"
            className="pl-9"
          />
        </div>
        <Seleccion
          value={rol}
          onChange={(e) => setRol(e.target.value)}
          aria-label="Filtrar por rol"
          className="w-48"
        >
          <option value="">Todos los roles</option>
          {ROLES.map((r) => (
            <option key={r.valor} value={r.valor}>
              {r.texto}
            </option>
          ))}
        </Seleccion>
        <Seleccion
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          aria-label="Filtrar por estado"
          className="w-44"
        >
          <option value="">Todos los estados</option>
          <option value={UserStatus.ACTIVE}>Activos</option>
          <option value={UserStatus.SUSPENDED}>Suspendidos</option>
        </Seleccion>
      </div>

      {usuarios.isLoading && <Cargando />}
      {usuarios.isError && (
        <ErrorCarga
          mensaje={getErrorMessage(usuarios.error)}
          onReintentar={() => void usuarios.refetch()}
        />
      )}

      {usuarios.data?.data.length === 0 && (
        <div className="mt-6">
          <Vacio
            icono={<UsersIcon className="h-6 w-6" />}
            titulo="Ningún usuario con esos filtros"
            descripcion="Prueba con otra búsqueda."
          />
        </div>
      )}

      <div className="mt-4 space-y-2">
        {usuarios.data?.data.map((usuario) => (
          <Fila key={usuario.id} usuario={usuario} onCredenciales={setCredenciales} />
        ))}
      </div>

      {usuarios.data?.hasMore && (
        <p className="mt-4 text-center text-sm text-kick-gray">
          Se muestran los 50 más recientes. Afina la búsqueda para ver otros.
        </p>
      )}

      <Modal
        abierto={!!credenciales}
        onCerrar={() => setCredenciales(null)}
        titulo="Contraseña restablecida"
        descripcion="Entrégasela al usuario. Está obligado a cambiarla al entrar."
        ancho="sm"
      >
        <div className="p-5">
          {credenciales && <TarjetaCredenciales credenciales={credenciales} />}
          <Boton className="mt-5 w-full" onClick={() => setCredenciales(null)}>
            Ya la copié
          </Boton>
        </div>
      </Modal>
    </div>
  );
}

function Fila({
  usuario,
  onCredenciales,
}: {
  usuario: UsuarioAdmin;
  onCredenciales: (c: Credenciales) => void;
}) {
  const avisos = useAvisos();
  const yo = useAuth((s) => s.user);
  const cambiarEstado = useCambiarEstadoUsuario();
  const restablecer = useRestablecerPassword();
  const [confirmando, setConfirmando] = useState(false);

  const suspendido = usuario.status === UserStatus.SUSPENDED;
  const rol = ROLES.find((r) => r.valor === usuario.role);
  const Icono = rol?.icono ?? UserRound;
  const puede = usePuede();
  // Ni a uno mismo ni a otra cuenta de administración: la API lo rechaza igual.
  const puedeSuspender =
    usuario.id !== yo?.id && usuario.role !== UserRole.ADMIN && puede('usuarios', 'bloquear');

  return (
    <article className="card-kick flex flex-wrap items-center gap-3 p-3.5">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-kick-light text-kick-gray">
        <Icono className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-kick-ink">{usuario.fullName}</p>
        <p className="text-sm text-kick-gray">
          <span className="font-mono">{usuario.username}</span>
          {usuario.phone && ` · ${usuario.phone}`}
          {usuario.merchantName && ` · ${usuario.merchantName}`}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-kick-light px-2 py-0.5 text-kick-gray">{rol?.texto}</span>
        <span
          className={cn('rounded-full px-2 py-0.5 font-semibold', ESTADOS[usuario.status]?.color)}
        >
          {ESTADOS[usuario.status]?.texto ?? usuario.status}
        </span>
        {usuario.mustChangePassword && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
            debe cambiar clave
          </span>
        )}
      </div>

      <span className="hidden text-xs text-kick-gray lg:block">
        {usuario.lastLoginAt ? `entró ${formatFecha(usuario.lastLoginAt)}` : 'nunca ha entrado'}
      </span>

      <div className="flex gap-1.5">
        {puede('usuarios', 'editar') && (
          <Boton
            variante="secundario"
            className="px-2.5 py-1.5 text-xs"
            icono={<KeyRound className="h-3.5 w-3.5" />}
            cargando={restablecer.isPending}
            onClick={() =>
              restablecer.mutate(usuario.id, {
                onSuccess: onCredenciales,
                onError: (error) => avisos.error(getErrorMessage(error)),
              })
            }
          >
            Clave
          </Boton>
        )}

        {puedeSuspender && (
          <Boton
            variante={suspendido ? 'secundario' : 'peligro'}
            className="px-2.5 py-1.5 text-xs"
            cargando={cambiarEstado.isPending}
            onClick={() => (suspendido ? reactivar() : setConfirmando(true))}
          >
            {suspendido ? 'Reactivar' : 'Suspender'}
          </Boton>
        )}
      </div>

      <ConfirmarModal
        abierto={confirmando}
        titulo={`Suspender a ${usuario.fullName}`}
        mensaje="Se cierran todas sus sesiones abiertas de inmediato y no podrá volver a entrar hasta que lo reactives."
        textoConfirmar="Suspender"
        cargando={cambiarEstado.isPending}
        onCancelar={() => setConfirmando(false)}
        onConfirmar={() =>
          cambiarEstado.mutate(
            { id: usuario.id, status: UserStatus.SUSPENDED },
            {
              onSuccess: () => {
                setConfirmando(false);
                avisos.exito(`${usuario.username} suspendido`);
              },
              onError: (error) => avisos.error(getErrorMessage(error)),
            },
          )
        }
      />
    </article>
  );

  function reactivar(): void {
    cambiarEstado.mutate(
      { id: usuario.id, status: UserStatus.ACTIVE },
      {
        onSuccess: () => avisos.exito(`${usuario.username} reactivado`),
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  }
}
